/**
 * App Builder Component Palette – Draggable component types for drag-and-drop onto the canvas.
 */
import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import {
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Description as FormIcon,
  Map as MapIcon,
  TextFields as RichTextIcon,
  Image as ImageIcon,
  GridView as GridIcon,
  TrendingUp as StatsIcon,
  List as ListIcon,
  Timeline as TimelineIcon,
  Chat as ChatIcon,
  Summarize as SummaryIcon,
} from '@mui/icons-material';
import { useDrag } from 'react-dnd';

const ITEM_TYPE = 'APP_BUILDER_COMPONENT';

const PALETTE_ITEMS = [
  { type: 'table', label: 'Table', icon: <TableIcon /> },
  { type: 'chart', label: 'Chart', icon: <ChartIcon /> },
  { type: 'form', label: 'Form', icon: <FormIcon /> },
  { type: 'map', label: 'Map', icon: <MapIcon /> },
  { type: 'rich_text', label: 'Rich Text', icon: <RichTextIcon /> },
  { type: 'image', label: 'Image', icon: <ImageIcon /> },
  { type: 'grid', label: 'Grid', icon: <GridIcon /> },
  { type: 'stats', label: 'Stats', icon: <StatsIcon /> },
  { type: 'list', label: 'List', icon: <ListIcon /> },
  { type: 'timeline', label: 'Timeline', icon: <TimelineIcon /> },
  { type: 'summary', label: 'Summary', icon: <SummaryIcon /> },
];

function DraggablePaletteItem({ item, onClick }) {
  const [{ isDragging }, drag] = useDrag({
    type: ITEM_TYPE,
    item: { componentType: item.type },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  return (
    <Paper
      ref={drag}
      elevation={0}
      onClick={() => onClick?.(item.type)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1.5,
        cursor: 'grab',
        opacity: isDragging ? 0.5 : 1,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:hover': { bgcolor: 'action.hover' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{item.icon}</Box>
      <Typography variant="body2" fontWeight={500}>{item.label}</Typography>
    </Paper>
  );
}

export default function AppBuilderComponentPalette({ onAddComponent }) {
  return (
    <Box
      sx={{
        width: 200,
        flexShrink: 0,
        borderRight: 1,
        borderColor: 'divider',
        p: 2,
        overflowY: 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
        Components
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Drag onto a panel zone, or click to add
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {PALETTE_ITEMS.map((item) => (
          <DraggablePaletteItem key={item.type} item={item} onClick={onAddComponent} />
        ))}
      </Box>
    </Box>
  );
}

export { ITEM_TYPE };
